import { useHomeCrumb } from "@/components/PageBreadcrumbs";
import {
  articleOptions,
  updateArticleReadLog,
  useArticle,
} from "@/api/functions/communication";
import ArticleViewer from "@/components/communication/ArticleViewer";
import PageBackButton from "@/components/PageBackButton";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import { firebaseTimestampToDate, NUMERIC_DATE_TIME_LOCALE } from "@/lib";
import { Breadcrumbs, Icon } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns-tz";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRouterStore } from "@/store/zustandStore";

export const Route = createFileRoute("/_portal/news/$articleId")({
  component: RouteComponent,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(articleOptions(params.articleId));
  },
});

function RouteComponent() {
  const { articleId } = Route.useParams();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { hasCommunication, visibleForUsers } = useWelcomeCommunicationAccess();
  const { data: article } = useArticle(articleId);

  const publishedDate = useMemo(
    () =>
      article.publishedAt ? firebaseTimestampToDate(article.publishedAt) : "",
    [article],
  );
  const showOriginalPublishDate =
    article.lastUpdated &&
    article.lastUpdated.at._seconds !== article.publishedAt?._seconds &&
    publishedDate;

  const homeCrumb = useHomeCrumb();
  const breadcrumbs = [
    ...homeCrumb,
    {
      label: t("SIDEBAR.NEWS"),
      path: `/news`,
    },
    {
      label: `${i18n.language === "en" ? article.title.en : article.title.no}`,
      path: `/news/${articleId}`,
    },
  ];

  useEffect(() => {
    // only register article as read after the page has been open for at least 1 second
    const timeout = setTimeout(() => {
      updateArticleReadLog(articleId).catch((err) => {
        console.error("Failed to log article read:", err);
      });
    }, 1000);

    return () => clearTimeout(timeout); // cancels if user navigates away
  }, [articleId]);

  if (!hasCommunication) return null;
  return (
    <div>
      {!visibleForUsers && (
        <p
          className="banner"
          style={{ marginBottom: "var(--wlcm-spacing-sm)" }}
        >
          <Icon name="info" /> {t("COMMUNICATION.HIDDEN_FOR_USERS_INFO")}
        </p>
      )}
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/communication",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <ArticleViewer
        title={i18n.language === "en" ? article.title.en : article.title.no}
        timestamp={article.lastUpdated?.at || article.publishedAt}
        author={article.author}
        content={
          i18n.language === "en" ? article.content.en : article.content.no
        }
        attachments={article.attachments}
        operational={article.disregardConsent}
        bannerImage={article.bannerImg}
      />
      {/* If article has been updated, render original publish date */}
      {showOriginalPublishDate && (
        <p style={{ opacity: 0.5, marginTop: "var(--wlcm-spacing-md)" }}>
          {t("COMMUNICATION.PUBLISHED_AT")}{" "}
          <time dateTime={format(publishedDate, "yyyy-MM-dd' 'HH:mm")}>
            {NUMERIC_DATE_TIME_LOCALE(i18n.language).format(publishedDate)}
          </time>
        </p>
      )}
    </div>
  );
}
